import { createSlice } from '@reduxjs/toolkit'

const initialState = {
    // Map tracking records by composite keys for fast lookup
    trackingRecordsMap: new Map(),
    // Raw tracking records from backend
    trackingRecords: null,
    // Active time windows for cleanup tracking
    activeTimeWindows: new Map(),
    // Background update interval ID
    backgroundUpdateInterval: null,
    // Settings
    updateIntervalMs: 10000, // 10 seconds
}

const feedbackTrackingSlice = createSlice({
    name: 'feedbackTracking',
    initialState,
    reducers: {
        // Set feedback tracking records from submission response
        setTrackingRecords: (state, action) => {
            state.trackingRecords = action.payload
            state.trackingRecordsMap = processFeedbackTrackingRecords(action.payload)
        },

        // Set background update interval ID
        setBackgroundUpdateInterval: (state, action) => {
            state.backgroundUpdateInterval = action.payload
        },

        // Clear all active tracking (for cleanup)
        clearActiveTracking: state => {
            state.activeTimeWindows = new Map()
            state.backgroundUpdateInterval = null
        },

        // Reset all tracking state
        reset: () => initialState,
    },
})

// Helper function to process tracking records into a lookup map
const processFeedbackTrackingRecords = trackingRecords => {
    const trackingMap = new Map()

    if (!trackingRecords || !Array.isArray(trackingRecords)) {
        return trackingMap
    }

    trackingRecords.forEach(record => {
        // Create composite keys for different feedback types
        const keys = []

        if (record.layer_id) {
            keys.push(`layer_${record.layer_id}`)

            if (record.cluster_id) {
                keys.push(`cluster_${record.layer_id}_${record.cluster_id}`)
            }

            if (record.motif_id) {
                keys.push(`motif_${record.motif_id}`)
            }
        }

        keys.forEach(key => {
            trackingMap.set(key, record)
        })
    })

    return trackingMap
}

export const {
    setTrackingRecords,
    addActiveTimeWindow,
    removeActiveTimeWindow,
    setBackgroundUpdateInterval,
    clearActiveTracking,
    reset,
} = feedbackTrackingSlice.actions

// Selectors
export const selectTrackingRecords = state => state.feedbackTracking.trackingRecords
export const selectTrackingRecordsMap = state => state.feedbackTracking.trackingRecordsMap
export const selectUpdateIntervalMs = state => state.feedbackTracking.updateIntervalMs

// Helper function to get tracking record for feedback item
export const getTrackingRecordForFeedback = (trackingMap, feedbackItem) => {
    if (!trackingMap || !feedbackItem) {
        return null
    }

    // Try different keys based on feedback type
    const possibleKeys = []

    if (feedbackItem.motif_id) {
        possibleKeys.push(`motif_${feedbackItem.motif_id}`)
    }

    if (feedbackItem.cluster_id) {
        possibleKeys.push(`cluster_${feedbackItem.layer_id}_${feedbackItem.cluster_id}`)
    }

    if (feedbackItem.layer_id) {
        possibleKeys.push(`layer_${feedbackItem.layer_id}`)
    }

    for (const key of possibleKeys) {
        const record = trackingMap.get(key)
        if (record) {
            return record
        }
    }

    return null
}

export default feedbackTrackingSlice.reducer
